/******************************************************************************

Copyright (c) 2016-2018, Highsoft

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY
CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

******************************************************************************/

// @format

// There be dragons here...

import { merge, isBasic, isArr } from "./highcharts-editor";

export default class Transform {
  constructor(){

    this.hasTransformedAdvanced = false;
  }
    /**
       * Merges/extends advanced meta
       *  1. Find the source
       *  2. If source is also extending:
       *      2a. Recursively merge source
       *  3. Remove the extends attribute from target
       *  4. Merge source children
       */
    mergeAdv(superset, dest, src, trigger) {
      let path = src.split('.'),
        current = superset;

      // console.log(
      //     'extending',
      //     (dest.meta.ns ? dest.meta.ns + '.' : '') + dest.meta.name,
      //     'with',
      //     src
      // );

      path.some(function(p) {
        if (current.subtree[p]) {
          current = current.subtree[p];
        } else {
          // console.log(
          //     'unable to resolve path for merge:',
          //     src,
          //     'from',
          //     dest.meta.name
          // );

          current = false;
          return true;
        }
      });
      // Stop from trying to extend this multiple times
      dest.meta.extends = dest.meta.extends.replace(src, '');

      if (current) {
        // Extend the source if needed
        this.extend(superset, current);

        // Unfortunatly we need to take series into special consideration
        // until we have a more robust way of handling its meta
        if (trigger && trigger.indexOf('series') === 0) {
          Object.keys(current.subtree || {}).forEach(function(key) {
            dest.subtree[key] =
              dest.subtree[key] || merge({}, current.subtree[key]);
            dest.subtree[key].meta.validFor =
              dest.subtree[key].meta.validFor || {};

            if (
              dest.meta.excludes &&
              Object.keys(dest.meta.excludes).length > 0
            ) {
              dest.subtree[key].meta.validFor[current.meta.name] = !dest.meta
                .excludes[key];
            } else {
              dest.subtree[key].meta.validFor[current.meta.name] = 1;
            }
          });

        } else if ((trigger && trigger.indexOf('plotOptions') === 0) || dest.meta.ns === undefined) {
          if (!dest.meta.validFor) dest.meta.validFor = {};
          dest.meta.validFor[dest.meta.name] = 1;

          if (dest.meta.ns === undefined) {
            merge(dest.subtree, current.subtree, false, dest.meta.excludes);
          } else {
            Object.keys(current.subtree || {}).forEach(function(key) {
              dest.subtree[key] =
                dest.subtree[key] || merge({}, current.subtree[key]);
              dest.subtree[key].meta.validFor =
                dest.subtree[key].meta.validFor || {};
    
              if (
                dest.meta.excludes &&
                Object.keys(dest.meta.excludes).length > 0
              ) {
                dest.subtree[key].meta.validFor[current.meta.name] = !dest.meta
                  .excludes[key];
              } else {
                dest.subtree[key].meta.validFor[current.meta.name] = 1;
              }
            }); 
          }

        } else {
          // Do actual extending
          merge(dest.subtree, current.subtree, false, dest.meta.excludes);
        }
      }
    }

    /**
        * Extend a node
        */
    extend(superset, node, trigger) {
      if (trigger === undefined) {
        if (node.meta.ns && node.meta.ns === "plotOptions") {
          trigger = 'plotOptions';
        }
      }
      if (node.meta.extends && node.meta.extends.length > 0) {
        node.meta.extends = node.meta.extends.replace('{', '').replace('}', '');
        if (trigger === 'series') {
          node.meta.extends += ',plotOptions.line';
        }
        
        node.meta.extends.split(',').forEach((part) => {
          if (part && part.length > 0) {
            this.mergeAdv(superset, node, part.trim(), trigger);
          }
        });
      }
    }

    /**
       * Transform the tree
       * - merges
       * - arrifies
       * - sorts
       *
       * Duplicating children is faster than arrifying and replacing
       *
       */
    transformAdv(input, onlyOnce, chartType) {
      let res;
      
      if (onlyOnce && this.hasTransformedAdvanced) {
        return input;
      }

      const visit = (node, pname) => {
        
        let children = (node.subtree = node.subtree || {});
        
        node.meta = node.meta || {};
        node.meta.default = node.meta.default;
        node.meta.ns = pname;
        node.children = [];

        if (node.meta && node.meta.products && !node.meta.products[chartType]) {
          return;
        }

        // Take care of merging
        this.extend(input, node, (pname ? pname + '.' : '') + node.meta.name);

        node.meta.hasSubTree = false;

        node.children = [];

        Object.keys(children).forEach(function(child) {
          if (Object.keys(children[child].subtree).length > 0) {
            node.meta.hasSubTree = true;
          }

          const childNode = visit(
            children[child],
            (pname ? pname + '.' : '') + (node.meta.name || '')
          );

          if (childNode) node.children.push(childNode);
        });

        node.children.sort(function(a, b) {
          return a.meta.name.localeCompare(b.meta.name);
        });

        if (node.children.length === 0) {
          node.meta.leafNode = true;
        }

        return node;
      }
      // console.time('tree transform');
      res = visit(input);
      // console.timeEnd('tree transform');

      return res;
    }

    // Removes all empty objects and arrays from the input object
    removeBlanks(input) {
      const rewind = (stack) => {
        if (!stack || stack.length === 0) return;

        let t = stack.pop();

        if (Object.keys(t).length === 0) {
          rewind(stack);
        } else {
          Object.keys(t || {}).forEach((key) => {
            let child = t[key];

            if (key[0] === '_') {
              delete t[key];
            } else if (
              child &&
              !isBasic(child) &&
              !isArr(child) &&
              Object.keys(child).length === 0
            ) {
              delete t[key];
            } else if (isArr(child) && child.length === 0) {
              delete t[key];
            } else if (isArr(child)) {
              child = child.map((sub) => {
                return this.removeBlanks(sub);
              });
            }
          });
        }

        rewind(stack);
      }

      const visit =(node, parentStack) => {
        parentStack = parentStack || [];

        if (node) {
          if (parentStack && Object.keys(node).length === 0) {
            rewind(parentStack.concat([node]));
          } else {
            Object.keys(node).forEach((key) => {
              let child = node[key];
              if (key[0] === '_') {
                rewind(parentStack.concat([node]));
              } else if (!isBasic(child) && !isArr(child)) {
                visit(child, parentStack.concat([node]));
              }
            });
          }
        }
      }

      visit(input);
      return input;
    }
}